import React, { FC, useEffect, useMemo, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { SuperAdminTaskProvider, useSuperAdminTask } from '~/contexts/SuperAdminTaskContext'
import { TaskContext } from '~/contexts/TaskContext'
import { Workspace, WorkspaceContext } from '~/contexts/WorkspaceContext'
import { CommonModal, ModalId } from '~/hooks/useModal'
import { CommandType, commandTypeFromJSON } from '~/protos/agents'
import {
  getConversationMessages,
  getTaskCreditUsage,
  TaskCreditUsage
} from '~/services/superAdminService'
import { TaskStatusDetailModal } from '../modals/TaskStatusDetailsModal'
import { TaskDisplay } from '../task/TaskDisplay'

// QA-type commands that store output in conversation messages rather than task responses
const QA_COMMAND_TYPES = [
  CommandType.QUESTION_ANSWERING,
  CommandType.MULTI_REPO_QA,
  CommandType.CODE_REVIEW,
  CommandType.GENERAL_MULTI_REPO_QA_DEBUG_AGENT
]

function isQaTypeTask(cmdType: string): boolean {
  const commandType = commandTypeFromJSON(cmdType)
  return QA_COMMAND_TYPES.includes(commandType)
}
function CreditUsagePanel({ taskId }: { taskId: number }) {
  const [creditData, setCreditData] = useState<TaskCreditUsage | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setLoading(true)
    setError(null)
    getTaskCreditUsage(taskId)
      .then(setCreditData)
      .catch(err => setError(err.message || 'Failed to load credit usage'))
      .finally(() => setLoading(false))
  }, [taskId])

  if (loading) {
    return (
      <div className='flex items-center gap-2 text-xs text-gray-400'>
        <div className='animate-spin rounded-full h-3 w-3 border-b border-gray-400' />
        Loading credit usage...
      </div>
    )
  }

  if (error || !creditData) return null

  const fmt = (n: number) => n.toLocaleString()

  return (
    <div className='flex flex-wrap items-center gap-x-4 gap-y-1 text-xs'>
      <span className='font-semibold text-purple-700 bg-purple-50 px-2 py-0.5 rounded'>
        {creditData.task.credits} credit{creditData.task.credits !== 1 ? 's' : ''}
      </span>
      <span className='text-gray-500'>
        {fmt(creditData.task.input_tokens)} in / {fmt(creditData.task.output_tokens)} out tokens
      </span>
      <span className='text-gray-400'>
        ({creditData.task.event_count} LLM call{creditData.task.event_count !== 1 ? 's' : ''})
      </span>
      {creditData.task.model_breakdown.length > 0 && (
        <span className='text-gray-400'>
          [
          {creditData.task.model_breakdown
            .map(m => `${m.model}: ${m.raw_credits.toFixed(2)}cr`)
            .join(', ')}
          ]
        </span>
      )}
      {creditData.conversation && (
        <span className='text-blue-600 bg-blue-50 px-2 py-0.5 rounded'>
          Conversation total: {creditData.conversation.credits} credit
          {creditData.conversation.credits !== 1 ? 's' : ''} (
          {fmt(creditData.conversation.input_tokens)} in /{' '}
          {fmt(creditData.conversation.output_tokens)} out tokens)
        </span>
      )}
    </div>
  )
}

interface SuperAdminTaskDisplayProps {
  taskId: number
  workspaceUuid?: string
  onClose: () => void
}

// Bridge component that provides regular TaskContext using SuperAdminTaskContext data
function TaskContextBridge({ children }: { children: React.ReactNode }) {
  const { task, messages, isLoading, error, refetch } = useSuperAdminTask()

  // Adapt SuperAdminTaskContext data to TaskContext format expected by TaskDisplay
  const taskContextValue = useMemo(
    () => ({
      currentTask: task && messages ? { task, messages } : undefined,
      currentTaskId: task?.task_id?.toString() || null,
      isLoadingCurrentTask: isLoading,
      currentTaskError: error,
      refetch
    }),
    [task, messages, isLoading, error, refetch]
  )

  return <TaskContext.Provider value={taskContextValue}>{children}</TaskContext.Provider>
}

// Component to display QA task conversation messages
function QaTaskDisplay({ onClose }: { onClose: () => void }) {
  const { task, isLoading: taskLoading, error: taskError } = useSuperAdminTask()
  const [conversationMessages, setConversationMessages] = useState<
    Array<{ role: string; content: string }>
  >([])
  const [messagesLoading, setMessagesLoading] = useState(false)
  const [messagesError, setMessagesError] = useState<string | null>(null)

  useEffect(() => {
    if (!task?.conversation_id) return

    setMessagesLoading(true)
    setMessagesError(null)

    getConversationMessages(task.conversation_id!)
      .then(response => {
        // API returns { messages: [...] }
        const messagesData = response?.messages || []

        // Extract text content from messages
        const formattedMessages = messagesData.map(msg => {
          let content = ''
          if (typeof msg.text === 'string' && msg.text) {
            content = msg.text
          } else if (msg.blocks && Array.isArray(msg.blocks)) {
            // Extract text from blocks
            content = (msg.blocks as Array<{ text?: { text?: string } | string }>)
              .map(block => {
                if (block.text) {
                  return typeof block.text === 'string' ? block.text : block.text.text || ''
                }
                return ''
              })
              .filter(Boolean)
              .join('\n\n')
          }
          return {
            role: msg.role || 'unknown',
            content
          }
        })
        setConversationMessages(formattedMessages)
      })
      .catch(err => {
        setMessagesError(err.message || 'Failed to load conversation messages')
      })
      .finally(() => {
        setMessagesLoading(false)
      })
  }, [task?.conversation_id])

  if (taskLoading) {
    return (
      <div className='flex items-center justify-center h-full'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-4' />
          <p className='text-gray-600 select-none'>Loading task...</p>
        </div>
      </div>
    )
  }

  if (taskError || !task) {
    return (
      <div className='flex items-center justify-center h-full'>
        <div className='text-center'>
          <p className='text-red-600 mb-4'>{taskError?.message || 'Task not found'}</p>
          <button
            onClick={onClose}
            className='px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded transition-colors'
          >
            Close
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className='h-full flex flex-col'>
      {/* Header */}
      <div className='flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white'>
        <button
          onClick={onClose}
          className='flex items-center text-gray-600 hover:text-gray-900 transition-colors'
        >
          <svg className='w-5 h-5 mr-2' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M15 19l-7-7 7-7'
            />
          </svg>
          Back
        </button>
        <div className='flex items-center gap-2'>
          <span
            className={`px-2 py-1 text-xs font-medium rounded ${
              task.status === 'COMPLETED'
                ? 'bg-green-100 text-green-700'
                : task.status === 'FAILED'
                  ? 'bg-red-100 text-red-700'
                  : 'bg-yellow-100 text-yellow-700'
            }`}
          >
            {task.status}
          </span>
        </div>
      </div>

      {/* Task Info */}
      <div className='px-4 py-3 bg-gray-50 border-b border-gray-200'>
        <div className='flex items-center gap-4 text-sm text-gray-600'>
          <span>
            <strong>Task #{task.task_id}</strong>
          </span>
          <span>Type: {task.cmd_type}</span>
          <span>Conversation: {task.conversation_id ? `#${task.conversation_id}` : 'N/A'}</span>
        </div>
        <div className='mt-2'>
          <CreditUsagePanel taskId={task.task_id} />
        </div>
      </div>

      {/* Messages */}
      <div className='flex-1 overflow-auto p-4'>
        {messagesLoading ? (
          <div className='flex items-center justify-center h-32'>
            <div className='animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600' />
          </div>
        ) : messagesError ? (
          <div className='text-center text-red-600 py-8'>{messagesError}</div>
        ) : conversationMessages.length === 0 ? (
          <div className='text-center text-gray-500 py-8'>No conversation messages found</div>
        ) : (
          <div className='space-y-4 max-w-4xl mx-auto'>
            {conversationMessages.map((msg, idx) => (
              <div
                key={idx}
                className={`rounded-lg p-4 ${
                  msg.role === 'user'
                    ? 'bg-blue-50 border border-blue-200'
                    : 'bg-gray-50 border border-gray-200'
                }`}
              >
                <div className='text-xs font-medium text-gray-500 mb-2 uppercase'>{msg.role}</div>
                {msg.content ? (
                  <div className='prose prose-sm max-w-none'>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                  </div>
                ) : (
                  <div className='text-gray-400 italic'>No content</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// Bridge component that provides WorkspaceContext for super-admin task viewing
// Only provides the workspace UUID needed for progress messages API calls
function WorkspaceBridge({
  workspaceUuid,
  children
}: {
  workspaceUuid?: string
  children: React.ReactNode
}) {
  // Create a minimal workspace context with just the UUID
  const workspaceContextValue = useMemo(
    () => ({
      workspaces: [] as Workspace[],
      currentWorkspace: workspaceUuid
        ? ({
            uuid: workspaceUuid,
            id: 0,
            name: 'Super Admin View',
            billing_account_id: 0
          } as unknown as Workspace)
        : null,
      isLoading: false,
      error: null,
      refetch: async () => {},
      switchToWorkspace: async () => {},
      createNewWorkspace: async () => {},
      switching: false,
      creating: false,
      updateCurrentWorkspaceRuntimeSetting: () => {}
    }),
    [workspaceUuid]
  )

  return (
    <WorkspaceContext.Provider value={workspaceContextValue}>{children}</WorkspaceContext.Provider>
  )
}

// Inner component that decides which display to use based on task type
function TaskDisplaySelector({
  workspaceUuid,
  onClose
}: {
  workspaceUuid?: string
  onClose: () => void
}) {
  const { task, isLoading, error } = useSuperAdminTask()

  if (isLoading) {
    return (
      <div className='flex items-center justify-center h-full'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-4' />
          <p className='text-gray-600 select-none'>Loading task...</p>
        </div>
      </div>
    )
  }

  if (error || !task) {
    return (
      <div className='flex items-center justify-center h-full'>
        <div className='text-center max-w-md'>
          <div className='text-red-500 text-4xl mb-4'>!</div>
          <p className='text-gray-800 font-medium mb-2'>Failed to load task</p>
          <p className='text-gray-500 text-sm'>
            {error?.message ||
              'Task data could not be retrieved. It may have been deleted or is inaccessible.'}
          </p>
          <button
            onClick={onClose}
            className='mt-4 px-4 py-2 bg-tokens-neutral-bg-grey-2 text-tokens-neutral-text-primary rounded-lg hover:bg-tokens-neutral-bg-grey-3 text-sm'
          >
            Close
          </button>
        </div>
      </div>
    )
  }

  // Check if this is a QA-type task that stores content in conversation messages
  const isQaTask = isQaTypeTask(task.cmd_type)

  // For QA tasks, show conversation messages
  if (isQaTask) {
    return <QaTaskDisplay onClose={onClose} />
  }

  // For other tasks, use the regular TaskDisplay. Indexes flow through React
  // Query keyed on workspace UUID — the WorkspaceBridge below overrides the
  // workspace context, so any consumer that calls `useIndexesContext` inside
  // automatically reads indexes for the super-admin-selected workspace.
  return (
    <WorkspaceBridge workspaceUuid={workspaceUuid}>
      <TaskContextBridge>
        <div className='flex flex-col h-full'>
          <div className='shrink-0 px-4 py-2 bg-gray-50 border-b border-gray-200'>
            <CreditUsagePanel taskId={task.task_id} />
          </div>
          <div className='flex-1 min-h-0'>
            <TaskDisplay onClose={onClose} />
          </div>
        </div>
        <CommonModal
          id={ModalId.TaskStatusDetails}
          Component={TaskStatusDetailModal as unknown as FC<Record<string, unknown>>}
        />
      </TaskContextBridge>
    </WorkspaceBridge>
  )
}

export function SuperAdminTaskDisplay({
  taskId,
  workspaceUuid,
  onClose
}: SuperAdminTaskDisplayProps) {
  return (
    <SuperAdminTaskProvider taskId={taskId}>
      <TaskDisplaySelector workspaceUuid={workspaceUuid} onClose={onClose} />
    </SuperAdminTaskProvider>
  )
}
